import { multiLocalFaqsFor } from '@alexiarest/shared';
import { useState } from 'react';
import type { HelpScreen, HelpVideo } from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { ASSISTANT_DISCLAIMER, isAssistantUnavailable } from '../api/assistantStatus';
import { errorMessage } from '../api/error';
import { veSeccion, type Acceso } from '../auth/roles';
import { useAssistantChat, useUpdateTenant } from '../api/hooks';
import type { AssistantMessage } from '../api/types';
import { ChatBubble, type ChatTurn } from '../components/assistant/ChatBubble';
import { ChatComposer } from '../components/assistant/ChatComposer';
import { ThinkingIndicator } from '../components/assistant/ThinkingIndicator';
import { Drawer } from '../components/Drawer';
import { IconoWhatsApp, WHATSAPP_HREF } from '../components/SupportButton';
import { useToast } from '../components/Toast';
import { useLocationScope } from '../context/LocationContext';
import { usePeriod } from '../context/PeriodContext';

/**
 * Panel de ayuda de cada pantalla (Alex, 5 sep 2026).
 *
 * Se abre con el boton «Ayuda» de la cabecera y ensenya, en este orden: los
 * videos de la pantalla en la que estas (los graba Alex; sin videos la
 * seccion no sale), como funciona, las preguntas frecuentes y un chat con
 * Alexia que sabe en que pantalla estas. Al pie, volver a ver el tour,
 * repetir el cuestionario inicial (solo el propietario) y WhatsApp.
 *
 * El contenido sale de shared/help.ts: es el mismo manual que recibe Alexia
 * en su prompt, asi que lo que dice el panel y lo que dice el chat cuadra.
 */

/** Iconos de las secciones del panel, en linea. */
function Icono({ tipo }: { tipo: 'video' | 'libro' | 'faq' | 'chat' | 'tour' | 'ajustes' | 'chevron' | 'flecha' }): JSX.Element {
  const comun = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (tipo) {
    case 'video':
      return (
        <svg {...comun}>
          <rect x="3" y="5" width="18" height="14" rx="3" />
          <path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'libro':
      return (
        <svg {...comun}>
          <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
          <path d="M4 19a2 2 0 0 1 2-2h13" />
        </svg>
      );
    case 'faq':
      return (
        <svg {...comun}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5v.2M12 17h.01" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...comun}>
          <path d="M4 5h16v10H9l-5 4z" />
          <path d="M8 9h8M8 12h5" />
        </svg>
      );
    case 'tour':
      return (
        <svg {...comun}>
          <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
          <path d="M9 4v14M15 6v14" />
        </svg>
      );
    case 'ajustes':
      return (
        <svg {...comun}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      );
    case 'flecha':
      return (
        <svg {...comun}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    default:
      return (
        <svg {...comun}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
  }
}

let contador = 0;
const nuevoId = (): string => {
  contador += 1;
  return `ayuda-${contador}`;
};

/**
 * De un enlace normal (YouTube, Loom, Vimeo) al que se puede incrustar.
 * Cualquier otro se ensenya como enlace: mejor eso que un iframe en blanco.
 */
export function embedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') return `https://www.youtube-nocookie.com/embed/${u.pathname.slice(1)}`;
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
      const m = u.pathname.match(/^\/(?:embed|shorts)\/([^/?]+)/);
      if (m) return `https://www.youtube-nocookie.com/embed/${m[1]}`;
      return null;
    }
    if (host === 'loom.com') {
      const m = u.pathname.match(/^\/(?:share|embed)\/([^/?]+)/);
      return m ? `https://www.loom.com/embed/${m[1]}` : null;
    }
    if (host === 'vimeo.com') {
      const m = u.pathname.match(/^\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    if (host === 'player.vimeo.com') return url;
    return null;
  } catch {
    return null;
  }
}

/** Ficheros de video servidos por nosotros (bucket de ayuda): van en <video>, no en iframe. */
export function esVideoDirecto(url: string): boolean {
  return /\.(mp4|webm|m4v)(\?|#|$)/i.test(url);
}

/** Un video: el nuestro en <video>, los de YouTube/Loom/Vimeo incrustados, el resto como enlace. */
function Reproductor({ video }: { video: HelpVideo }): JSX.Element {
  if (esVideoDirecto(video.url)) {
    return (
      <div className="help__video-marco">
        {/* key: al cambiar de video el navegador descarga el nuevo, no reusa el elemento a medias */}
        {/* Sin «Descargar» (mejora 63 de Alex): se ven aqui; velocidad, pantalla
            completa y PiP siguen. El menu contextual tambien lo ofrecia. */}
        <video
          key={video.url}
          controls
          controlsList="nodownload"
          preload="metadata"
          playsInline
          src={video.url}
          onContextMenu={(e) => e.preventDefault()}
        >
          <a href={video.url}>Ver el vídeo</a>
        </video>
      </div>
    );
  }
  const src = embedUrl(video.url);
  if (src) {
    return (
      <div className="help__video-marco">
        <iframe
          src={src}
          title={video.title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <a href={video.url} target="_blank" rel="noopener noreferrer" className="btn btn--ghost btn--sm">
      Ver el vídeo
    </a>
  );
}

/**
 * Los tutoriales de una pantalla: UN reproductor y la lista debajo. Cargar
 * seis <video> a la vez (aunque sea solo la cabecera) es tirar datos, y en
 * un panel de 30rem seis marcos de 16:9 no caben ni con scroll.
 */
function Videoteca({ videos }: { videos: HelpVideo[] }): JSX.Element {
  const [activo, setActivo] = useState(0);
  const video = videos[Math.min(activo, videos.length - 1)];
  return (
    <figure className="help__video">
      <Reproductor video={video} />
      <figcaption>
        {video.title}
        {video.duration ? ` · ${video.duration}` : ''}
      </figcaption>
      {videos.length > 1 && (
        <ol className="help__videos" aria-label="Tutoriales de esta pantalla">
          {videos.map((v, i) => (
            <li key={v.url}>
              <button
                type="button"
                className={`help__video-item ${i === activo ? 'is-activo' : ''}`.trim()}
                onClick={() => setActivo(i)}
                aria-current={i === activo ? 'true' : undefined}
              >
                <span className="help__video-num" aria-hidden="true">
                  {i === activo ? <Icono tipo="video" /> : i + 1}
                </span>
                <span className="help__video-titulo">{v.title}</span>
                {v.duration && <span className="help__video-dur">{v.duration}</span>}
              </button>
            </li>
          ))}
        </ol>
      )}
    </figure>
  );
}

export function HelpPanel({
  open,
  onClose,
  screen,
  role,
  acceso,
  onTour,
}: {
  open: boolean;
  onClose: () => void;
  /** null = esta pantalla no tiene manual (tesoreria, admin...): ayuda general. */
  screen: HelpScreen | null;
  role?: string;
  /** Rol y permisos: deciden si esta persona tiene chat con Alexia. */
  acceso?: Acceso;
  onTour: () => void;
}): JSX.Element | null {
  const esPropietario = role === 'owner' || role === 'admin';
  // Ni el encargado ni cocina tienen asistente (21 ago / 7 sep), y un rol
  // personalizado lo tiene si se lo han dado: videos y manual siempre.
  const conChat = veSeccion(acceso ?? { role }, 'asistente');
  // La ayuda del multi-local solo se ensenya a quien tiene mas de un local.
  const { locations } = useLocationScope();
  const variosLocales = locations.filter((l) => l.active).length > 1;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={screen ? `Ayuda · ${screen.title}` : 'Ayuda'}
      subtitle={
        screen?.summary ??
        'Esta pantalla todavía no tiene guía. Puedes preguntarle a Alexia o escribirnos.'
      }
      width="30rem"
      focusBody={false}
    >
      <div className="help">
        {/*
          * Orden de la mejora 57 de Alex (6 sep 2026): lo primero es verlo
          * (tutoriales), luego la duda concreta (FAQ), luego preguntar a
          * Alexia, y al final el resto. «Cómo funciona» sigue aquí pero
          * plegado: ocupaba media pantalla y casi nadie lo lee entero.
          */}
        {screen && screen.videos.length > 0 && (
          <section className="help__seccion" aria-label="Tutoriales rápidos">
            <h3 className="help__h"><Icono tipo="video" /> Tutoriales rápidos</h3>
            {/* key: cambiar de pantalla vuelve al primer video de la nueva */}
            <Videoteca key={screen.id} videos={screen.videos} />
          </section>
        )}

        {screen && screen.faqs.length > 0 && (
          <section className="help__seccion" aria-label="Preguntas frecuentes">
            <h3 className="help__h"><Icono tipo="faq" /> Preguntas frecuentes</h3>
            {screen.faqs.map((faq) => (
              <details key={faq.q} className="help__faq">
                <summary>
                  <span>{faq.q}</span>
                  <span className="help__faq-chevron"><Icono tipo="chevron" /></span>
                </summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </section>
        )}

        {screen && variosLocales && multiLocalFaqsFor(screen.id).length > 0 && (
          <section className="help__seccion" aria-label="Con varios locales">
            <h3 className="help__h"><Icono tipo="faq" /> Con varios locales</h3>
            {multiLocalFaqsFor(screen.id).map((faq) => (
              <details key={faq.q} className="help__faq">
                <summary>
                  <span>{faq.q}</span>
                  <span className="help__faq-chevron"><Icono tipo="chevron" /></span>
                </summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </section>
        )}

        {screen && screen.howItWorks.length > 0 && (
          <section className="help__seccion" aria-label="Cómo funciona">
            <details className="help__faq help__faq--como" key={screen.id}>
              <summary>
                <span><Icono tipo="libro" /> ¿Cómo funciona esta sección?</span>
                <span className="help__faq-chevron"><Icono tipo="chevron" /></span>
              </summary>
              {screen.howItWorks.map((parrafo, i) => (
                <p key={i}>{parrafo}</p>
              ))}
            </details>
          </section>
        )}

        {/* `key`: cambiar de pantalla remonta el chat. Una respuesta en vuelo
            de la pantalla anterior muere con el componente en vez de colarse
            en la conversacion nueva. */}
        {conChat && <HelpChat key={screen?.id ?? 'general'} screen={screen} activo={open} />}

        <section className="help__seccion" aria-label="Más ayuda">
          <h3 className="help__h">Más ayuda</h3>
          <div className="help__pie">
            <button type="button" className="help__tile" onClick={onTour}>
              <span className="help__tile-icono"><Icono tipo="tour" /></span>
              <span className="help__tile-texto">
                <span className="help__tile-titulo">Volver a ver el tour</span>
                <span className="help__tile-sub">Un minuto por las pantallas</span>
              </span>
              <Icono tipo="flecha" />
            </button>
            {esPropietario && <RepetirConfiguracion onDone={onClose} />}
            <a className="help__tile" href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">
              <span className="help__tile-icono help__tile-icono--wa"><IconoWhatsApp /></span>
              <span className="help__tile-texto">
                <span className="help__tile-titulo">Escríbenos por WhatsApp</span>
                <span className="help__tile-sub">Te contestamos nosotros</span>
              </span>
              <Icono tipo="flecha" />
            </a>
          </div>
        </section>
      </div>
    </Drawer>
  );
}

/** Reabre el cuestionario inicial en el paso 2. Util si se salto todo, y para la demo. */
function RepetirConfiguracion({ onDone }: { onDone: () => void }): JSX.Element {
  const update = useUpdateTenant();
  const toast = useToast();
  return (
    <button
      type="button"
      className="help__tile"
      disabled={update.isPending}
      onClick={() =>
        update.mutate(
          { setup: { step: 2 } },
          { onSuccess: onDone, onError: (error) => toast.fail(error) },
        )
      }
    >
      <span className="help__tile-icono"><Icono tipo="ajustes" /></span>
      <span className="help__tile-texto">
        <span className="help__tile-titulo">
          {update.isPending ? 'Abriendo…' : 'Repetir la configuración inicial'}
        </span>
        <span className="help__tile-sub">Facturación, ventas, TPV y objetivos</span>
      </span>
      <Icono tipo="flecha" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Chat acotado a la pantalla                                          */
/* ------------------------------------------------------------------ */

function HelpChat({ screen, activo }: { screen: HelpScreen | null; activo: boolean }): JSX.Element {
  const { period } = usePeriod();
  const { locationId } = useLocationScope();
  const chat = useAssistantChat();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [failed, setFailed] = useState(false);

  const unavailable = chat.isError && isAssistantUnavailable(chat.error);

  function send(history: ChatTurn[]): void {
    setTurns(history);
    setFailed(false);
    const messages: AssistantMessage[] = history.map((t) => ({ role: t.role, content: t.content }));
    chat.mutate(
      { messages, period, screen: screen?.id, locationId },
      {
        onSuccess: (data) =>
          setTurns((prev) => [
            ...prev,
            { id: nuevoId(), role: 'assistant', content: data.reply, toolCalls: data.toolCalls },
          ]),
        onError: () => setFailed(true),
      },
    );
  }

  function ask(question: string): void {
    const text = question.trim();
    if (text === '' || chat.isPending) return;
    setDraft('');
    send([...turns, { id: nuevoId(), role: 'user', content: text }]);
  }

  return (
    <section className="help__seccion help__chat" aria-label="Pregunta a Alexia" aria-busy={chat.isPending}>
      <h3 className="help__h"><Icono tipo="chat" /> Pregúntale a Alexia</h3>
      {turns.length === 0 && (
        <>
          <p className="help__p">
            Sabe cómo funciona esta pantalla y tiene tus datos. Pregúntale cómo se hace algo o qué
            significa un número.
          </p>
          {isDemoMode && (
            <p className="help__nota">
              En la demo, Alexia responde con las preguntas frecuentes y los datos de ejemplo.
            </p>
          )}
          <ul className="help__sugerencias" aria-label="Preguntas sugeridas">
            {(screen?.suggestions ?? []).slice(0, 2).map((q) => (
              <li key={q}>
                <button type="button" className="help__sugerencia" disabled={chat.isPending || unavailable} onClick={() => ask(q)}>
                  <span>{q}</span>
                  <Icono tipo="flecha" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {turns.length > 0 && (
        <div className="help__log">
          {turns.map((turn) => (
            <ChatBubble key={turn.id} turn={turn} />
          ))}
        </div>
      )}

      {chat.isPending && <ThinkingIndicator />}

      {failed && !chat.isPending && (
        <div className="chat__error" role="alert">
          <p className="alert alert--bad">
            <span className="alert__icon" aria-hidden="true">
              !
            </span>
            <span>{errorMessage(chat.error)}</span>
          </p>
          {!unavailable && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => send(turns)}>
              Reintentar
            </button>
          )}
        </div>
      )}

      {activo && (
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSend={() => ask(draft)}
          busy={chat.isPending}
          disabled={unavailable}
        />
      )}
      <p className="help__nota">{ASSISTANT_DISCLAIMER}</p>
    </section>
  );
}
